/**
 * Grouping a view.
 *
 * The same shape as the sort mutations next door, and for the same reasons:
 * nothing is optimistic, because a group by reorders the whole table and the
 * client holds a hundred rows of it; and the cached view is patched rather than
 * invalidated, because the answer says exactly what changed.
 *
 * The one thing a group by does that a sort does not is change what the rows
 * *look* like -- there are columns of headings beside them now. That falls out
 * of the same patch: the view's `group_bys` are read by the grid and are in the
 * row query's signature, so one write both redraws the surface and refetches
 * the rows in their new order.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { getApi, unwrap } from '@/api/client'
import { qk } from '@/api/query-keys'
import type { View, ViewGroupBy } from '@/api/types'
import { DEFAULT_GROUP_WIDTH } from '../grid/grouping'
import type { SortDirection } from './sort-mutations'
import { withGroupBy, withGroupByOrder, withoutGroupBy } from './view-cache'

export interface ViewGroupByMutations {
  add: (field: number, order: SortDirection) => void
  change: (groupById: number, changes: { field?: number; order?: SortDirection }) => void
  remove: (groupById: number) => void
  /** The terms in the order they should be applied, first one first. */
  prioritize: (groupByIds: number[]) => void
  /** Whether a change is on its way, so the controls can say so. */
  isPending: boolean
}

export function useViewGroupBys(tableId: number, viewId: number): ViewGroupByMutations {
  const queryClient = useQueryClient()
  const key = qk.views(tableId)

  const store = (groupBy: ViewGroupBy): void => {
    queryClient.setQueryData<View[]>(key, (views) =>
      views === undefined ? views : withGroupBy(views, viewId, groupBy),
    )
  }

  const add = useMutation({
    mutationFn: (body: { field: number; order: SortDirection }): Promise<ViewGroupBy> =>
      unwrap(
        getApi().POST('/api/database/views/{view_id}/group_bys/', {
          params: { path: { view_id: viewId } },
          // The width and the sort type are the model's own defaults, sent
          // because the endpoint declares them required. Repeating a default
          // is the lesser of the two evils: the alternative is a request the
          // generated client refuses to build.
          body: { ...body, width: DEFAULT_GROUP_WIDTH, type: 'default' },
        }),
      ),
    onSuccess: store,
  })

  const change = useMutation({
    mutationFn: ({
      groupById,
      changes,
    }: {
      groupById: number
      changes: { field?: number; order?: SortDirection }
    }): Promise<ViewGroupBy> =>
      unwrap(
        getApi().PATCH('/api/database/views/group_by/{view_group_by_id}/', {
          params: { path: { view_group_by_id: groupById } },
          body: changes,
        }),
      ),
    onSuccess: store,
  })

  const remove = useMutation({
    mutationFn: (groupById: number): Promise<unknown> =>
      unwrap(
        getApi().DELETE('/api/database/views/group_by/{view_group_by_id}/', {
          params: { path: { view_group_by_id: groupById } },
        }),
      ),
    // A 204 that says nothing, so what went is read from what was asked.
    onSuccess: (_answer, groupById) => {
      queryClient.setQueryData<View[]>(key, (views) =>
        views === undefined ? views : withoutGroupBy(views, viewId, groupById),
      )
    },
  })

  const prioritize = useMutation({
    mutationFn: (ids: number[]): Promise<unknown> =>
      unwrap(
        getApi().POST('/api/database/views/{view_id}/group_bys/prioritize/', {
          params: { path: { view_id: viewId } },
          body: { view_group_by_ids: ids },
        }),
      ),
    onMutate: (ids) => {
      // The one change here that *is* applied before the answer. Reordering the
      // terms does reorder the rows -- it bumps the row query's signature and
      // the window refetches -- but the list somebody is dragging has to follow
      // the drag, and it is the list rather than the rows that is being moved.
      const previous = queryClient.getQueryData<View[]>(key)
      queryClient.setQueryData<View[]>(key, (views) =>
        views === undefined ? views : withGroupByOrder(views, viewId, ids),
      )
      return { previous }
    },
    onError: (_error, _ids, context) => {
      queryClient.setQueryData<View[] | undefined>(key, context?.previous)
    },
  })

  return {
    add: (field, order) => {
      add.mutate({ field, order })
    },
    change: (groupById, changes) => {
      change.mutate({ groupById, changes })
    },
    remove: (groupById) => {
      remove.mutate(groupById)
    },
    prioritize: (ids) => {
      prioritize.mutate(ids)
    },
    isPending:
      add.isPending || change.isPending || remove.isPending || prioritize.isPending,
  }
}
